import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import { useManageReferrals } from "@/modules/lead-page/hooks/manage-referrals.hook";
import { DynamicForm } from "@/modules/common/components/dynamic-form/dynamic-form";
import { TOAST_IDS } from "@/modules/common/types";
import { getErrorMessage, mapReferralValues } from "@/modules/common/utils";
import { showToast } from "@/modules/common/utils";
import { useMutation } from "@tanstack/react-query";
import { addLeadReferral } from "@/modules/lead-page/api/lead-page.v2.api";
import { queryClient } from "@/queryClient";
import { ToastType } from "@/modules/common/types";
import { AddReferralRequest } from "@/modules/lead-page/dtos/index-v2";
import { useLeadContext } from "@/modules/lead-page/context/lead-page.context";
import { useMemo, useState } from "react";
import { SingleAutoCompleteSelectChip } from "@/modules/common/components/input/single-autocomplete-select";

const AddReferral = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { formFields, referralUsers } = useManageReferrals();

  const [referralFormValues, setReferralFormValues] =
    useState<AddReferralRequest>({});

  const referralUsersOptions = useMemo(
    () =>
      referralUsers?.data?.map((user) => ({
        value: user.uid,
        label: user.name,
      })) || [],
    [referralUsers?.data]
  );

  const { lead } = useLeadContext();
  const { mutate: addReferral, isLoading: isAddingReferral } = useMutation({
    mutationFn: ({
      leadUId,
      data,
    }: {
      leadUId: string;
      data: AddReferralRequest;
    }) => addLeadReferral(leadUId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["lead-referrals", lead?.data?.lead.uid],
      });
      showToast({
        type: ToastType.SUCCESS,
        message: "Referral added successfully",
        toastId: TOAST_IDS.ADD_REFERRAL_SUCCESS,
      });
      setOpen(false);
      setReferralFormValues({});
      referralUsers.setValue(null);
    },
    onError: (error: {
      response: { data: { message: string; statusCode: number } };
    }) => {
      showToast({
        type: ToastType.ERROR,
        message: getErrorMessage(error),
        toastId: TOAST_IDS.ADD_REFERRAL_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
      setOpen(false);
      setReferralFormValues({});
      referralUsers.setValue(null);
    },
  });

  const handleAddLeadMethodChange = (values?: { [key: string]: any }) => {
    let referralData: AddReferralRequest = {};
    if (referralUsers.value) {
      referralData.referrerUId = referralUsers.value;
    }
    if (values) {
      referralData = {
        ...referralData,
        ...mapReferralValues(referralFormValues),
      };
    }

    const data: AddReferralRequest = {
      ...referralData,
    };

    addReferral({
      leadUId: lead?.data?.lead.uid ?? "",
      data,
    });
  };

  return (
    <ResponsiveEditDialog
      open={open}
      setOpen={setOpen}
      header="Add Referral"
      hideModalFooter
      modalFooter={false}
      width={1000}
    >
      <SingleAutoCompleteSelectChip
        name="referralUser"
        size="small"
        options={referralUsersOptions}
        label="Select Referral User"
        onChange={(value) => {
          referralUsers.setValue(value.toString());
        }}
        onInputChange={(value) => referralUsers.setSearch(value)}
        value={referralUsers.value?.toString()}
      />
      {formFields && (
        <DynamicForm
          handleConfirm={handleAddLeadMethodChange}
          onChange={(values) => {
            setReferralFormValues(values);
          }}
          isLoadingSubmition={isAddingReferral}
          handleClose={() => setOpen(false)}
          formFields={formFields.data}
        />
      )}
    </ResponsiveEditDialog>
  );
};

export default AddReferral;
